import { componentMixin } from './component-mixin.js';
import { reactive, dispose, effect } from '../reactivity/index.js';
import { attachHooks, detachHooks, registerHook, triggerHook } from './component-bus';

let i = 0;

const components = {
    created: 0,
    destroyed: 0,
    internal: new WeakMap(),
};

const initComponent = (component) => {
    const id = `${component.constructor.name}-${i++}`;
    components.created += 1;
    components.internal.set(component, {
        id,
        setupInvoked: false,
        destroyInvoked: false,
        effectCleanupFunctions: null,
    });
};

class Component {
    constructor() {
        initComponent(this);
    }

    async setup(...args) {
        // prevent double invocation
        if (components.internal.get(this).setupInvoked) {
            throw new Error(`setup() can only be invoked once for ${this.constructor.name}`);
        }
        components.internal.get(this).setupInvoked = true;

        // props as function
        if (typeof this.props === 'function') {
            this.props = this.props();
        }

        // before hook
        if (this.onBeforeSetup) {
            await this.onBeforeSetup(...args);
        }

        // merge options
        let options;
        if (args[0] && args[0].options) {
            const a = args[0];
            ({ options } = a);
        }
        this.options = Object.freeze(Object.assign({}, this.options, options));

        // merge props and make reactive
        let props;
        if (args[0] && args[0].props) {
            const a = args[0];
            ({ props } = a);
        }
        props = Object.assign({}, this.props, props);
        const propKeys = Object.keys(props);
        if (propKeys.length !== 0) {
            props = reactive(props);
            this.props = props;
        }

        // main setup hook
        if (this.onSetup) {
            await this.onSetup(...args);
        }

        // after setup hook
        if (this.onAfterSetup) {
            await this.onAfterSetup(...args);
        }

        // prop effects
        if (this.props) {
            propKeys.forEach(async (propKey) => {
                const methodPostfix = `${propKey[0].toUpperCase()}${propKey.substr(1)}`;
                const methodSetName = `set${methodPostfix}`;
                const methodOnName = `on${methodPostfix}`;
                const methodOffName = `off${methodPostfix}`;
                const methodWhileName = `while${methodPostfix}`;

                const setHandler = this[methodSetName] ? this[methodSetName].bind(this) : undefined;
                const onHandler = this[methodOnName] ? this[methodOnName].bind(this) : undefined;
                const offHandler = this[methodOffName] ? this[methodOffName].bind(this) : undefined;
                const whileHandler = this[methodWhileName] ? this[methodWhileName].bind(this) : undefined;

                // no need if we don't have hooks
                if (!setHandler && !onHandler && !offHandler && !whileHandler) {
                    return;
                }

                let effectCleanupFunctions = components.internal.get(this).effectCleanupFunctions;
                if (!effectCleanupFunctions) {
                    effectCleanupFunctions = [];
                    components.internal.get(this).effectCleanupFunctions = effectCleanupFunctions;
                }

                effectCleanupFunctions.push(
                    effect(() => this.props[propKey], {
                        setHandler,
                        onHandler,
                        offHandler,
                        whileHandler,
                    }),
                );
            });
        }

        // Bus hooks
        attachHooks(this);

        return this;
    }

    destroy(...args) {
        // prevent double invocation
        if (components.internal.get(this).destroyInvoked) {
            throw new Error(`destroy() can only be invoked once for ${this.constructor.name}`);
        }
        components.internal.get(this).destroyInvoked = true;

        // Bus hooks
        detachHooks(this);

        // before hook
        if (this.onBeforeDestroy) {
            this.onBeforeDestroy(...args);
        }
        // destroy all children directly attached to `this`
        Object.entries(this).forEach(([key, val]) => {
            if (val instanceof Component && !components.internal.get(val).destroyInvoked) {
                val.destroy();
                this[key] = null;
            }
        });

        // cleanup effects
        const effectCleanupFunctions = components.internal.get(this).effectCleanupFunctions;
        if (effectCleanupFunctions) {
            const l = effectCleanupFunctions.length;
            for (let i = 0; i < l; i += 1) {
                effectCleanupFunctions[i]();
            }
            components.internal.get(this).effectCleanupFunctions = null;
        }

        // main hook
        if (this.onDestroy) {
            this.onDestroy(...args);
        }

        // dispose props
        if (this.props) {
            dispose(this.props);
            this.props = null;
        }

        if (this.options) {
            this.options = null;
        }

        components.destroyed += 1;
    }
}

export { registerHook, triggerHook };
export { Component, componentMixin };
